import { Fragment, MouseEvent, ReactElement } from 'react'
import { NavLink } from 'react-router-dom'

import DropdownMenu, { LinkProps } from '@/layouts/partials/DropdownMenu'
import { classNames, cn } from '@/lib/misc/Utils'
import ArrowTopRightOnSquare from '@icons/arrow-top-right-on-square'
import { DropdownContent } from '@/layouts/partials/DropdownContent'
import ArrowDownIcon from '@icons/chevron-down'

export interface NavigationItemInterface {
  id?: string
  icon?: ReactElement
  to?: string
  children?: NavigationItemInterface[]
  onClick?: Function | undefined
  title?: string
  links?: LinkProps[]
  isHidden?: boolean
  external?: boolean
  content?: ReactElement
  isActive?: boolean
  alignment?: 'left' | 'right'
  highlight?: boolean
  showArrow?: boolean
}

export interface NavigationGroupInterface {
  className?: string
  items: NavigationItemInterface[]
}

const NavigationGroupItem = ({
  to,
  children,
  className,
  external,
  isActive: isMenuActive,
  ...props
}: {
  children: ReactElement
  to?: string
  className?: string
  external?: boolean
  onClick?: (event: MouseEvent<HTMLElement>) => void
  isActive?: boolean
}) =>
  !!to ? (
    <NavLink
      to={to}
      className={({ isActive }) =>
        classNames(
          (isActive || isMenuActive) &&
            'bg-accent-10 text-accent-60! hover:bg-accent-20!',
          className
        )
      }
      target={external ? '_blank' : '_self'}
      {...props}
    >
      {children}
    </NavLink>
  ) : (
    <span className={className} {...props}>
      {children}
    </span>
  )

export const NavigationGroup = ({
  group,
  onGroupItemClick,
  className,
}: {
  group: NavigationGroupInterface
  onGroupItemClick?: Function
  className?: string
}) => {
  const handleOnGroupItemClick = (
    event: MouseEvent<HTMLElement>,
    item: NavigationItemInterface
  ) => {
    item.onClick?.(event)

    onGroupItemClick?.()
  }

  return (
    <div className={cn('flex items-center', className)}>
      {group.items.map((item, index) => {
        const groupItem = (
          <NavigationGroupItem
            to={item.to}
            data-cy={item.id}
            className={cn(
              'flex flex-row items-center space-x-1 transition-colors group',
              'mx-auto h-9 cursor-pointer rounded-lg px-3 text-[14px] font-medium leading-5',

              item.isHidden && 'hidden',
              item.highlight
                ? 'bg-brand-2 text-white opacity-100! hover:bg-brand-2/50'
                : 'text-neutral-60 hover:text-neutral-100 hover:bg-neutral-10',
              (!!item.links || !!item.external) && 'pr-2',
              group.className
            )}
            external={item.external}
            onClick={(event: MouseEvent<HTMLElement>) =>
              handleOnGroupItemClick(event, item)
            }
          >
            <>
              {!!item.icon && (
                <span className="size-4 text-neutral-60 group-hover:text-neutral-60">
                  {item.icon}
                </span>
              )}

              {!!item.title && (
                <span className="inline-flex items-center h-4">
                  {item.title}
                </span>
              )}

              {(!!item.links || !!item.showArrow) && (
                <span className="opacity-50 size-4 inline-flex">
                  <ArrowDownIcon className="h-full w-full" />
                </span>
              )}

              {!!item.external && item.showArrow !== false && (
                <span className="opacity-50 size-4">
                  <ArrowTopRightOnSquare className="h-full w-full" />
                </span>
              )}
            </>
          </NavigationGroupItem>
        )

        const body = item.content ? (
          <DropdownContent
            content={item.content}
            onClose={() => onGroupItemClick?.()}
          >
            {groupItem}
          </DropdownContent>
        ) : item.links ? (
          <DropdownMenu
            width={180}
            id={item.id}
            links={item.links}
            alignment={item.alignment}
          >
            {groupItem}
          </DropdownMenu>
        ) : (
          groupItem
        )

        return <Fragment key={item.id ?? index}>{body}</Fragment>
      })}
    </div>
  )
}
